import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';

import { CustomDialog } from '../CustomDialog';
import { RankBadge } from '../RankBadge';
import { rankDialogAtom } from '../../atoms';
import { Context, useAuth } from '../../context/auth';
import { RANK_POINTS, RANK_TIERS, getRankTier } from '../../helpers';
import styles from './style';

// The points of the top 10, on 2 rows of 5.
const POINTS_ROWS = [RANK_POINTS.slice(0, 5), RANK_POINTS.slice(5, 10)];

// How the ranks work, opened from a rank badge or the rank card (same content as the app):
// the points of the weekly tops, then the ladder of the ranks with the one of the user.
const RankDialog: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const { user: me } = useAuth() as Context;
  const [rankUser, setRankUser] = useAtom(rankDialogAtom);

  const isMe = !!rankUser && rankUser.id === me.id;
  const rank = getRankTier(rankUser ? rankUser.rank : '').rank;
  const title = isMe ? t('admin.rankDialog.title') : (rankUser ? rankUser.username : '');

  const close = useCallback(() => {
    setRankUser(null);
  }, [setRankUser]);

  const ladder = useMemo(() => RANK_TIERS.map((tier) => {
    const points = `${tier.points} ${t('admin.rankDialog.points')}`;
    const isCurrent = tier.rank === rank;
    return {
      rank: tier.rank,
      isCurrent,
      label: isMe && isCurrent ? `${points} · ${t('admin.rankDialog.you')}` : points,
    };
  }), [isMe, rank, t]);

  return (
    <CustomDialog open={!!rankUser} onClose={close} paperSx={styles.paper} contentSx={styles.content} sx={styles.dialog}>
      <IconButton onClick={close} aria-label={t('admin.common.close')} sx={styles.close}>
        <Close fontSize="small" />
      </IconButton>
      <Typography component="h2" sx={styles.title}>{title}</Typography>
      <Typography sx={styles.description}>{t('admin.rankDialog.description')}</Typography>

      <Box sx={styles.pointsGrid}>
        {POINTS_ROWS.map((row, rowIndex) => (
          <Box key={`points-${row[0]}`} sx={styles.pointsRow}>
            {row.map((points, index) => (
              <Box key={`top-${points}`} sx={styles.pointsCell}>
                <Box component="span" sx={styles.pointsTop}>
                  {`${t('admin.rankDialog.top')} ${rowIndex * row.length + index + 1}`}
                </Box>
                <Box component="span" sx={styles.pointsValue}>{points}</Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={styles.ladder}>
        {ladder.map((tier) => (
          <Box key={tier.rank} sx={styles.ladderRow}>
            <RankBadge rank={tier.rank} />
            <Box component="span" sx={tier.isCurrent ? styles.ladderPointsCurrent : styles.ladderPoints}>
              {tier.label}
            </Box>
          </Box>
        ))}
      </Box>

      <Button disableRipple onClick={close} sx={styles.button}>{t('admin.rankDialog.understood')}</Button>
    </CustomDialog>
  );
});

export default RankDialog;
